import React, { useRef, useState } from 'react';
import { Bold, Italic, List, Eye, Code2 } from 'lucide-react';
import { sanitizeRichHtml } from '../lib/sanitize';

/**
 * Lightweight HTML editor tuned for the Qualiopi templates used by the
 * programme PDF generator. Supports the subset of HTML that ReportLab's
 * Paragraph accepts: <b>, <i>, <br/>, <br/> lists with `•` bullets.
 *
 * Features:
 *  - Toolbar: Bold, Italic, Bullet list (inserts "• " at line start)
 *  - Auto-insert <br/> on Enter for ReportLab compatibility
 *  - Live HTML preview toggle
 *  - Restore defaults button (per field)
 */
export default function RichEditor({ value, onChange, defaultValue, placeholder, rows = 8, testId }) {
  const taRef = useRef(null);
  const [mode, setMode] = useState('edit'); // 'edit' | 'preview'

  const wrap = (open, close) => {
    const ta = taRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const sel = value.slice(start, end) || 'texte';
    const next = value.slice(0, start) + open + sel + close + value.slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      ta.focus();
      ta.selectionStart = start + open.length;
      ta.selectionEnd = start + open.length + sel.length;
    });
  };

  const insertBullet = () => {
    const ta = taRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    // Find start of line
    const before = value.slice(0, start);
    const lineStart = before.lastIndexOf('\n') + 1;
    const prefix = '• ';
    const next = value.slice(0, lineStart) + prefix + value.slice(lineStart);
    onChange(next);
    requestAnimationFrame(() => {
      ta.focus();
      ta.selectionStart = ta.selectionEnd = start + prefix.length;
    });
  };

  const restore = () => {
    if (defaultValue !== undefined && window.confirm('Restaurer le texte par défaut ? La version actuelle sera remplacée.')) {
      onChange(defaultValue);
    }
  };

  // For preview: convert \n → <br/> (ReportLab treats <br/> as line-breaks)
  const previewHtml = (value || '').replace(/\n/g, '<br/>');

  return (
    <div className="rounded-xl border border-[#EFF0F6] bg-white overflow-hidden" data-testid={testId}>
      <div className="flex items-center justify-between border-b border-[#EFF0F6] px-2 py-1.5 bg-[#FAFAFE]">
        <div className="flex items-center gap-0.5">
          <ToolbarBtn icon={Bold} label="Gras" onClick={() => wrap('<b>', '</b>')} />
          <ToolbarBtn icon={Italic} label="Italique" onClick={() => wrap('<i>', '</i>')} />
          <ToolbarBtn icon={List} label="Puce" onClick={insertBullet} />
          <span className="w-px h-5 bg-[#EFF0F6] mx-1" />
          <button type="button" onClick={() => setMode(mode === 'edit' ? 'preview' : 'edit')} className={`h-7 px-2 rounded-md text-[11px] font-semibold inline-flex items-center gap-1 ${mode === 'preview' ? 'bg-[#EEF2FF] text-[#4F46E5]' : 'text-[#6B6C85] hover:bg-[#F3F4F8]'}`} title="Basculer édition / aperçu">
            {mode === 'edit' ? <><Eye className="w-3.5 h-3.5" /> Aperçu</> : <><Code2 className="w-3.5 h-3.5" /> Éditer</>}
          </button>
        </div>
        {defaultValue !== undefined && (
          <button type="button" onClick={restore} className="h-7 px-2 rounded-md text-[10.5px] font-semibold text-[#8E8FA5] hover:text-[#4F46E5]">
            Restaurer par défaut
          </button>
        )}
      </div>
      {mode === 'edit' ? (
        <textarea
          ref={taRef}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          rows={rows}
          placeholder={placeholder}
          className="w-full px-3 py-2.5 text-[13px] leading-[1.55] text-[#1A1B2E] bg-white outline-none resize-y font-mono"
          data-testid={testId ? `${testId}-textarea` : undefined}
          spellCheck
        />
      ) : (
        <div
          className="px-3 py-2.5 text-[13px] leading-[1.55] text-[#1A1B2E] bg-white min-h-[160px] prose-sm"
          style={{ whiteSpace: 'normal' }}
          dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(previewHtml) || '<span class="text-[#8E8FA5] italic">Rien à afficher…</span>' }}
          data-testid={testId ? `${testId}-preview` : undefined}
        />
      )}
      <div className="px-3 py-1.5 text-[10.5px] text-[#8E8FA5] bg-[#FAFAFE] border-t border-[#EFF0F6]">
        Balises supportées : <code>&lt;b&gt;</code>, <code>&lt;i&gt;</code>, saut de ligne (Entrée), puces « • ».
      </div>
    </div>
  );
}

function ToolbarBtn({ icon: Icon, label, onClick }) {
  return (
    <button type="button" onClick={onClick} title={label}
      className="h-7 w-7 rounded-md text-[#6B6C85] hover:bg-[#F3F4F8] hover:text-[#4F46E5] flex items-center justify-center">
      <Icon className="w-3.5 h-3.5" />
    </button>
  );
}
